import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, FileWarning, Ban, Loader2, CheckCircle2 } from "lucide-react";
import SmartBackLink from "@/components/common/SmartBackLink";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { usePageMeta } from "@/hooks/usePageMeta";

const USER_ISSUES = ["Fraud", "Harassment", "Spam", "Fake account", "Policy violation", "Other"];
const CONTENT_ISSUES = ["Fake load", "Scam", "Spam", "Inappropriate", "Policy violation", "Other"];


type Report = {
  id: string;
  report_type: string;
  target_identifier: string;
  issue_type: string;
  status: string;
  created_at: string;
};

type Block = {
  id: string;
  blocked_identifier: string;
  created_at: string;
};

export default function SafetyReportPage() {
  usePageMeta({
    title: "Safety & Reporting | LaneRunner",
    description: "Report users or content, block users, or request account deletion on LaneRunner.",
  });
  const { user } = useAuth();

  // Form state
  const [userForm, setUserForm] = useState({ target: "", issue: "", details: "" });
  const [contentForm, setContentForm] = useState({ target: "", issue: "", details: "" });
  const [blockTarget, setBlockTarget] = useState("");

  const [submitting, setSubmitting] = useState<"user" | "content" | "block" | null>(null);
  const [confirmKind, setConfirmKind] = useState<"user" | "content" | null>(null);

  const [reports, setReports] = useState<Report[]>([]);
  const [blocks, setBlocks] = useState<Block[]>([]);

  async function loadData() {
    if (!user) return;
    const thirtyDaysAgo = new Date(Date.now() - 30 * 86400 * 1000).toISOString();
    const [r, b] = await Promise.all([
      supabase
        .from("user_reports")
        .select("id, report_type, target_identifier, issue_type, status, created_at")
        .eq("reporter_id", user.id)
        .gte("created_at", thirtyDaysAgo)
        .order("created_at", { ascending: false }),
      supabase
        .from("user_blocks")
        .select("id, blocked_identifier, created_at")
        .eq("blocker_id", user.id)
        .is("unblocked_at", null)
        .order("created_at", { ascending: false }),
    ]);
    if (r.data) setReports(r.data as Report[]);
    if (b.data) setBlocks(b.data as Block[]);
  }

  useEffect(() => { loadData(); /* eslint-disable-next-line */ }, [user?.id]);

  async function submitReport(kind: "user" | "content") {
    const f = kind === "user" ? userForm : contentForm;
    setSubmitting(kind);
    try {
      const { data, error } = await supabase.functions.invoke("submit-safety-report", {
        body: {
          report_type: kind,
          target_identifier: f.target.trim(),
          issue_type: f.issue,
          details: f.details.trim(),
        },
      });
      if (error || (data as any)?.error) throw new Error((data as any)?.error || error?.message);
      toast.success("Report submitted. Our team will review it.");
      if (kind === "user") setUserForm({ target: "", issue: "", details: "" });
      else setContentForm({ target: "", issue: "", details: "" });
      loadData();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not submit report");
    } finally {
      setSubmitting(null);
      setConfirmKind(null);
    }
  }

  async function blockUser() {
    if (!user || !blockTarget.trim()) return;
    setSubmitting("block");
    try {
      const identifier = blockTarget.trim();
      // best-effort resolve
      const { data: resolved } = await supabase.rpc("resolve_block_target", { _identifier: identifier });
      const { error } = await supabase.from("user_blocks").insert({
        blocker_id: user.id,
        blocked_identifier: identifier,
        blocked_user_id: (resolved as string | null) ?? null,
      });
      if (error) {
        if (error.code === "23505") toast.error("That user is already blocked.");
        else throw error;
      } else {
        toast.success("User blocked.");
        setBlockTarget("");
        loadData();
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not block user");
    } finally {
      setSubmitting(null);
    }
  }

  async function unblock(id: string) {
    const { error } = await supabase
      .from("user_blocks")
      .update({ unblocked_at: new Date().toISOString() })
      .eq("id", id);
    if (error) toast.error("Could not unblock");
    else { toast.success("Unblocked."); loadData(); }
  }

  const userValid = userForm.target.trim() && userForm.issue && userForm.details.trim();
  const contentValid = contentForm.target.trim() && contentForm.issue && contentForm.details.trim();

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 py-6 sm:py-10">
        <div className="mb-4"><SmartBackLink /></div>
        <header className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Safety & Reporting</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Report users or content, or block users from contacting you.
          </p>
        </header>

        {/* Section 0 — Account Deletion */}
        <p className="text-sm text-muted-foreground mb-5">
          To delete your account, go to <Link to="/billing" className="font-medium text-primary hover:underline">Billing → Delete Account</Link>.
        </p>

        {/* Section 1 — Report User */}
        <Card className="mb-5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ShieldAlert className="h-4 w-4 text-primary" /> Report User
            </CardTitle>
            <CardDescription>Report a user for fraud, scams, harassment, or policy violations.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label>User to report (email or company name)</Label>
              <Input value={userForm.target} onChange={(e) => setUserForm({ ...userForm, target: e.target.value })} placeholder="e.g. broker@example.com or Acme Logistics" maxLength={255} />
            </div>
            <div>
              <Label>Type of issue</Label>
              <Select value={userForm.issue} onValueChange={(v) => setUserForm({ ...userForm, issue: v })}>
                <SelectTrigger><SelectValue placeholder="Select issue" /></SelectTrigger>
                <SelectContent>
                  {USER_ISSUES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Details</Label>
              <Textarea value={userForm.details} onChange={(e) => setUserForm({ ...userForm, details: e.target.value.slice(0, 500) })} rows={4} placeholder="Describe what happened…" />
              <p className="text-xs text-muted-foreground mt-1">{userForm.details.length}/500</p>
            </div>
            <Button disabled={!userValid || submitting !== null} onClick={() => setConfirmKind("user")}>
              {submitting === "user" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Submit report
            </Button>
          </CardContent>
        </Card>

        {/* Section 2 — Report Content */}
        <Card className="mb-5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <FileWarning className="h-4 w-4 text-primary" /> Report Content
            </CardTitle>
            <CardDescription>Report a suspicious load, fake post, or inappropriate content.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label>Reference (load number, post URL)</Label>
              <Input value={contentForm.target} onChange={(e) => setContentForm({ ...contentForm, target: e.target.value })} placeholder="e.g. LR-AB12CD or https://…" maxLength={255} />
            </div>
            <div>
              <Label>Type of issue</Label>
              <Select value={contentForm.issue} onValueChange={(v) => setContentForm({ ...contentForm, issue: v })}>
                <SelectTrigger><SelectValue placeholder="Select issue" /></SelectTrigger>
                <SelectContent>
                  {CONTENT_ISSUES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Details</Label>
              <Textarea value={contentForm.details} onChange={(e) => setContentForm({ ...contentForm, details: e.target.value.slice(0, 500) })} rows={4} placeholder="Describe the issue…" />
              <p className="text-xs text-muted-foreground mt-1">{contentForm.details.length}/500</p>
            </div>
            <Button disabled={!contentValid || submitting !== null} onClick={() => setConfirmKind("content")}>
              {submitting === "content" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Submit report
            </Button>
          </CardContent>
        </Card>

        {/* Section 3 — Block User */}
        <Card className="mb-5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Ban className="h-4 w-4 text-primary" /> Block User
            </CardTitle>
            <CardDescription>Block a user from contacting you or appearing in your loads.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-col sm:flex-row gap-2">
              <Input value={blockTarget} onChange={(e) => setBlockTarget(e.target.value)} placeholder="Email or company name" maxLength={255} />
              <Button onClick={blockUser} disabled={!blockTarget.trim() || submitting !== null} variant="destructive">
                {submitting === "block" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Ban className="h-4 w-4 mr-2" />} Block
              </Button>
            </div>

            {blocks.length > 0 && (
              <div className="pt-3 border-t">
                <p className="text-xs font-medium text-muted-foreground mb-2">Currently blocked</p>
                <ul className="space-y-1.5">
                  {blocks.map((b) => (
                    <li key={b.id} className="flex items-center justify-between text-sm bg-muted/40 rounded px-3 py-2">
                      <span className="truncate">{b.blocked_identifier}</span>
                      <Button size="sm" variant="ghost" onClick={() => unblock(b.id)}>Unblock</Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent reports */}
        {reports.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Your recent reports (last 30 days)</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {reports.map((r) => (
                  <li key={r.id} className="flex items-start justify-between gap-3 text-sm border-b last:border-b-0 pb-2 last:pb-0">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{r.issue_type} — {r.target_identifier}</div>
                      <div className="text-xs text-muted-foreground">
                        {r.report_type === "user" ? "User report" : "Content report"} · {new Date(r.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <Badge variant={r.status === "resolved" ? "default" : r.status === "reviewed" ? "secondary" : "outline"}>
                      {r.status === "resolved" && <CheckCircle2 className="h-3 w-3 mr-1" />}
                      {r.status}
                    </Badge>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>

      <AlertDialog open={confirmKind !== null} onOpenChange={(o) => !o && setConfirmKind(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit this report?</AlertDialogTitle>
            <AlertDialogDescription>
              Our Trust & Safety team will review your submission. False or abusive reports may result in account action.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting !== null}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirmKind && submitReport(confirmKind)} disabled={submitting !== null}>
              {submitting !== null && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Submit
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
